import { FieldType, SectionFieldCollection } from '../magic-report.type';
import { DeclarativeLayoutConfig } from './DeclarativeLayoutRenderer';
import { ProjectSummary } from '../../../project/project.type';
import { formatNegativeWithBrackets } from '../../../../common/utils/common.util';
import {
  LIEN_WAIVERS_SINGLE_ABOVE_TABLE_COMMENT,
  LIEN_WAIVERS_SINGLE_CONDITIONAL_COMMENT,
  LIEN_WAIVERS_SINGLE_NO_WAIVER_COMMENT,
  LIEN_WAIVERS_SINGLE_UNCONDITIONAL_COMMENT,
} from './lien-waivers-comment.util';

/**
 * Configuration interface for section-specific behavior
 * Allows each section to define its own logic via declarative layout configuration
 */
export interface SectionConfig {
  /**
   * Generate default text based on selected statuses
   * Used to populate content fields when status buttons are toggled
   */
  getDefaultText?: (groupName: string, selectedStatuses: string[]) => string;

  /**
   * Auto-selection rules for multi-select toggle buttons
   * Example: selecting "Notarized" should auto-select "Signed"
   */
  autoSelectRules?: Record<string, { option: string; autoSelect: string[] }>;

  /**
   * Default options per field (fallback if not provided via fieldOptions prop)
   */
  defaultOptions?: Record<string, string[]>;

  /**
   * Suffix used to identify content fields related to status fields
   * Example: 'Content' means 'ContractorSignature' pairs with 'ContractorSignatureContent'
   */
  contentFieldSuffix?: string;

  /**
   * Function to identify comment fields
   * Used to filter and render comments section separately
   */
  isCommentField?: (fieldName: string, fieldType: FieldType) => boolean;
}

/**
 * Complete section configuration including metadata and behavior
 */
export interface CompleteSectionConfig {
  /** Unique identifier for the section */
  id: string;
  /** Display title */
  title: string;
  /** Function to generate default data */
  getDefaultData: (projectSummary?: any) => SectionFieldCollection;
  /** Section-specific behavior configuration */
  config: SectionConfig;
  /** Field-specific options for Select/Multiselect fields */
  fieldOptions?: Record<string, string[]>;
  /** Custom accordion styling */
  accordionSx?: any;
  /** Custom title variant (default: 'h5') */
  titleVariant?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  /** Custom title color (default: 'primary') */
  titleColor?: string;
  /** Layout configuration for declarative rendering */
  layout?: DeclarativeLayoutConfig;
  /** When true, hides the section-level bookmark toggle in the accordion header (for sections with no bookmarkable fields) */
  hideActionItemBookmark?: boolean;
}

/**
 * Configuration for Notary and Signatures section
 */
export const NOTARY_SIGNATURES_CONFIG: SectionConfig = {
  getDefaultText: (groupName: string, selectedStatuses: string[]) => {
    if (groupName === 'ContractorSignature') {
      const hasNotarized = selectedStatuses.includes('Notarized');
      const hasSigned = selectedStatuses.includes('Signed');

      if (hasNotarized) {
        // Both signed and notarized (Notarized auto-selects Signed)
        return 'The Application for Payment has been signed and dated by the Contractor and notarized as required.';
      } else if (hasSigned) {
        // Only signed, not notarized
        return 'The Application for Payment has been signed by the Contractors but not notarized. We leave the signature requirements as a condition for funding the Applications to the discretion of the Lender.';
      } else {
        // Not signed
        return 'The Application for Payment has not been signed or notarized by the Contractor. We leave the signature requirements as a condition for funding the Applications to the discretion of the Lender.';
      }
    } else if (groupName === 'ArchEngSignature') {
      const isSigned = selectedStatuses.includes('Signed');
      const isReviewed = selectedStatuses.includes('Architect Reviewed');
      const isUnsure = selectedStatuses.includes('Unsure if Architect Reviewed');

      if (isSigned && isReviewed) {
        // Signed AND architect is reviewing - DEFAULT
        return "The Architect of Record has certified the Amount of the Contractor's current Application.";
      } else if (isReviewed && !isSigned) {
        // Reviewed + NOT Signed
        return 'We understand that the Architect of Record is reviewing the Applications for Payment, but we have not received a copy of the certified document. We leave the Certification requirements as a condition for funding the Application to the discretion of the Lender.';
      } else if (!isSigned && isUnsure) {
        // Not signed AND unsure if reviewing
        return "We are unaware if the Architect of Record is, or is not, reviewing the Application for Payment from the Contractor, or is providing Construction Administration services to fulfill their contractual obligations to the Borrower and/or confirm the quality and strict compliance of the Work to the Plans and Specifications. The funding of the current Application without Certification by the Architect is left to the Lender's discretion.";
      } else if (!isSigned && !isReviewed) {
        // Not signed AND not reviewing
        return "We understand that the Architect of Record is not reviewing the Applications for Payment from the Contractor. We recommend that they do so in order to provide subsequent quality control to that provided by the Contractor. The funding of each Application without Certification by the Architect is left to the Lender's discretion.";
      } else {
        // Default fallback (should not happen with new validation, but kept for safety)
        return "The Architect of Record has certified the Amount of the Contractor's current Application.";
      }
    }
    return '';
  },

  autoSelectRules: {
    ContractorSignature: { option: 'Notarized', autoSelect: ['Signed'] },
  },

  defaultOptions: {
    ContractorSignature: ['Signed', 'Notarized'],
    ArchEngSignature: ['Signed', 'Architect Reviewed', 'Unsure if Architect Reviewed'],
  },

  contentFieldSuffix: 'Content',

  isCommentField: (fieldName: string, fieldType: FieldType) =>
    fieldType === FieldType.TextArray && fieldName.toLowerCase().includes('comment'),
};

/**
 * Configuration for Schedule section
 * Uses mostly default behavior with standard comment detection
 */
export const SCHEDULE_CONFIG: SectionConfig = {
  contentFieldSuffix: 'Content',

  isCommentField: (fieldName: string, fieldType: FieldType) =>
    fieldType === FieldType.TextArray && fieldName.toLowerCase().includes('comment'),
};

/**
 * Default configuration for sections without specific config
 */
export const DEFAULT_SECTION_CONFIG: SectionConfig = {
  contentFieldSuffix: 'Content',

  isCommentField: (fieldName: string, fieldType: FieldType) =>
    fieldType === FieldType.TextArray && fieldName.toLowerCase().includes('comment'),

  defaultOptions: {},
};

export const DOCUMENTS_RECEIVED_STATUS_OPTIONS = ['Yes', 'No', 'N/A'];

export const DOCUMENTS_RECEIVED_DEFAULT_TYPES = [
  { fieldName: 'CivilPlans', label: 'Civil Plans' },
  { fieldName: 'ArchitecturalPlans', label: 'Architectural Plans' },
  { fieldName: 'StructuralPlans', label: 'Structural Plans' },
  { fieldName: 'MechanicalPlans', label: 'Mechanical Plans' },
  { fieldName: 'PlumbingPlans', label: 'Plumbing Plans' },
  {
    fieldName: 'BuildersRiskGeneralLiabilityCerts',
    label: "Builder's Risk and General Liability Certs.",
  },
  { fieldName: 'FormSurvey', label: 'Form Survey' },
  { fieldName: 'ElectricalPlans', label: 'Electrical Plans' },
  { fieldName: 'Specifications', label: 'Specifications' },
  { fieldName: 'OwnerContractorAgreement', label: 'Owner Contractor Agreement' },
  { fieldName: 'SiteDevelopmentPermit', label: 'Site Development Permit' },
  { fieldName: 'BuildingPermit', label: 'Building Permit' },
  {
    fieldName: 'LoanDocumentsHardCost',
    label: 'Loan documents related to Hard Cost funding',
  },
] as const;

export const DOCUMENTS_RECEIVED_FIELD_NAMES = DOCUMENTS_RECEIVED_DEFAULT_TYPES.map(t => t.fieldName);

export const ALL_DOCS_DEFAULT_COMMENT =
  'We will request a copy of the remainder of the documents from the Borrower before the next application for payment.';

export const LOAN_DOCS_COMMENT_NO_TEXT =
  "We also have not been provided with the section of the loan documents which outline the lender's requirements for retainage held, construction deadlines, funding schedules and dates, release of retainage, or documentation required for final payment to the contractor. Should the lender wish for us to compare these loan requirements to those of the owner / contractor agreement, we request they provide that particular section of the loan document which outline those items. We will then inform the lender of potential discrepancies.";

export const LOAN_DOCS_COMMENT_YES_TEXT =
  "We have been provided with the section of the loan documents which outline the lender's requirements for retainage held, construction deadlines, funding schedules and dates, release of retainage, and documentation required for final payment to the contractor. Except as noted elsewhere in this report, the funding is consistent with the loan documents that have been provided to CDCC.";

export const DOCUMENTS_RECEIVED_INTRO_ALL_NO_TEXT =
  'We have received no plans, specifications or contracts as requested, but have used the plans at the site to perform our review. We will request of the Borrower that we receive the following documents before the next application.';

export const DOCUMENTS_RECEIVED_INTRO_SOME_RECEIVED_TEXT =
  'We have received the following plans, specifications, and contracts in order to perform our review of the project.';

/**
 * ============================================================================
 * Plan/Cost Review Issues Constants
 * ============================================================================
 */
export const PLAN_COST_REVIEW_TOGGLE_OPTIONS = ['G702/Budget Congruent', 'G702/Budget Not Congruent'] as const;
export type PlanCostReviewStatusOption = (typeof PLAN_COST_REVIEW_TOGGLE_OPTIONS)[number];

export const PLAN_COST_REVIEW_NO_REVIEW_TEXT =
  'Please note that according to our Agreement with the lender, we did not perform a Cost Review to confirm whether the budget is adequate to perform the construction work described in the construction documents, or whether the individual line items of the Schedule of Values were Front End Loaded.  We will review and monitor the construction work in comparison to each Scheduled Value / line item budget by a Percentage of Completion Method assuming that the Schedule of Values / line item budgets are acceptable to the lender.';

export const PLAN_COST_REVIEW_CONGRUENT_PRIMARY_TEXT =
  "Since this was our first review of an Application for Payment and our first visit to the construction site, we compared the Contractor's Schedule of Values and Overall Hard Cost Budget to the Budgets reviewed in our Plan / Cost Review and confirmed that Retainage is being held according to the Owner / Contractor and Loan Agreements.  We found Budgets were congruent and Retainage is being held according to the Agreements.";

export const PLAN_COST_REVIEW_NOT_CONGRUENT_PRIMARY_TEXT =
  'Since this was our first review of an Application for Payment and our first visit to the construction site, we compared the Contractor\'s Schedule of Values / line item budgets and the Overall Hard Cost Budget to the Budgets reviewed in our Plan / Cost Review and confirmed that Retainage is being held according to the Owner / Contractor and Loan Agreements.  We found Budgets have differed slightly from our Plan / Cost Review and the differences did not "Front End Load" the Schedule of Values / line item budgets. Front End Loading is a process in which the Contractor increases the budgets drawn early in the construction progress in order to receive funds in excess of the Work performed. We further found that Retainage is being held according to the Agreements.';

export const PLAN_COST_REVIEW_CONCLUSION_TEXT =
  'There are no other outstanding concerns related to our Initial Review of Budgets or our Plan / Cost Review.';

export const getPlanCostReviewDefaultTexts = (reviewingPlanOrCost?: boolean | null, status?: PlanCostReviewStatusOption) => {
  if (!reviewingPlanOrCost) {
    return {
      primaryText: PLAN_COST_REVIEW_NO_REVIEW_TEXT,
      secondaryText: '',
    };
  }

  if (status === PLAN_COST_REVIEW_TOGGLE_OPTIONS[1]) {
    return {
      primaryText: PLAN_COST_REVIEW_NOT_CONGRUENT_PRIMARY_TEXT,
      secondaryText: PLAN_COST_REVIEW_CONCLUSION_TEXT,
    };
  }

  return {
    primaryText: PLAN_COST_REVIEW_CONGRUENT_PRIMARY_TEXT,
    secondaryText: PLAN_COST_REVIEW_CONCLUSION_TEXT,
  };
};

export const getDefaultPlanCostReviewData = (projectSummary?: ProjectSummary): SectionFieldCollection => {
  const reviewingPlanOrCost = projectSummary?.reviewingPlanOrCost === true;
  const defaultStatus = reviewingPlanOrCost ? PLAN_COST_REVIEW_TOGGLE_OPTIONS[0] : '';
  const { primaryText, secondaryText } = getPlanCostReviewDefaultTexts(
    reviewingPlanOrCost,
    reviewingPlanOrCost ? PLAN_COST_REVIEW_TOGGLE_OPTIONS[0] : undefined,
  );

  return {
    fields: [
      {
        fieldName: 'PlanCostReviewStatus',
        fieldType: FieldType.Select,
        value: defaultStatus,
        addToActionItems: false,
      },
      {
        fieldName: 'PlanCostReviewPrimaryText',
        fieldType: FieldType.Text,
        value: primaryText,
        addToActionItems: false,
      },
      {
        fieldName: 'PlanCostReviewSecondaryText',
        fieldType: FieldType.Text,
        value: reviewingPlanOrCost ? secondaryText : '',
        addToActionItems: false,
      },
      {
        fieldName: 'PlanCostReviewComments',
        fieldType: FieldType.TextArray,
        entries: [],
      },
    ],
  };
};

/**
 * ============================================================================
 * Default Data Generators
 * ============================================================================
 */

/**
 * Default data generator for Notary and Signatures section
 */
export const getDefaultNotaryAndSignaturesData = (): SectionFieldCollection => ({
  fields: [
    {
      // Multi-G702 reports only: whether the statement covers every application ('All') or all
      // but one ('Some'). Defaults to 'All'; hidden for single-G702 reports.
      fieldName: 'ContractorAllApplications',
      fieldType: FieldType.Select,
      value: 'All',
      addToActionItems: false,
    },
    {
      fieldName: 'ContractorSignature',
      fieldType: FieldType.Select,
      value: 'Signed,Notarized', // Default: Both signed and notarized
      addToActionItems: false,
    },
    {
      fieldName: 'ContractorSignatureContent',
      fieldType: FieldType.Text,
      value: 'The Application for Payment has been signed and dated by the Contractor and notarized as required.',
      addToActionItems: false,
    },
    {
      // See ContractorAllApplications above.
      fieldName: 'ArchEngAllApplications',
      fieldType: FieldType.Select,
      value: 'All',
      addToActionItems: false,
    },
    {
      fieldName: 'ArchEngSignature',
      fieldType: FieldType.Select,
      value: 'Signed,Architect Reviewed', // Default: Signed and architect is reviewing
      addToActionItems: false,
    },
    {
      fieldName: 'ArchEngSignatureContent',
      fieldType: FieldType.Text,
      value: "The Architect of Record has certified the Amount of the Contractor's current Application.",
      addToActionItems: false,
    },
    {
      fieldName: 'Comments',
      fieldType: FieldType.TextArray,
      entries: [],
    },
  ],
});

/**
 * Default data generator for Schedule section
 */
export const CONSTRUCTION_PROGRESS_SCHEDULE_HAS_DATES_TEXT = 'We have received a copy of the Construction Progress Schedule as follows:';
export const CONSTRUCTION_PROGRESS_SCHEDULE_NO_DATES_TEXT =
  'We have not received a copy of the Construction Progress Schedule and will request one from the Borrower before our next site visit. The following dates are estimates on our part based upon the information that we have received and our experience with this type of project.';

export const getConstructionProgressScheduleText = (commencementDate?: string, completionDate?: string): string =>
  commencementDate && completionDate ? CONSTRUCTION_PROGRESS_SCHEDULE_HAS_DATES_TEXT : CONSTRUCTION_PROGRESS_SCHEDULE_NO_DATES_TEXT;

export const getDefaultScheduleData = (projectSummary?: any): SectionFieldCollection => ({
  fields: [
    // Date fields from projectSummary
    {
      fieldName: 'CommencementDate',
      fieldType: FieldType.Date,
      value: projectSummary?.commencementDate || '',
      addToActionItems: false,
    },
    // Inspector-only estimate; never shown in report text. Starts blank; required when CommencementDate is blank.
    {
      fieldName: 'CDCCEstimatedCommencementDate',
      fieldType: FieldType.Date,
      value: '',
      addToActionItems: false,
    },
    {
      fieldName: 'OriginalCompletionDate',
      fieldType: FieldType.Date,
      value: projectSummary?.originalCompletionDateEstimated || '',
      addToActionItems: false,
    },
    {
      fieldName: 'RevisedCompletionDate',
      fieldType: FieldType.Date,
      value: projectSummary?.revisedCompletionDate || '',
      addToActionItems: false,
    },
    {
      fieldName: 'BankLoanExpirationDate',
      fieldType: FieldType.Date,
      value: projectSummary?.bankLoanExpirationDate || '',
      addToActionItems: false,
    },
    {
      fieldName: 'CDCCEstimatedCompletionDate',
      fieldType: FieldType.Date,
      value: projectSummary?.cdccEstimatedCompletionDate || '',
      addToActionItems: false,
    },

    // Text field
    {
      fieldName: 'LiquidatedDamagesPerContract',
      fieldType: FieldType.Text,
      value: '',
      addToActionItems: false,
    },

    // Select fields (dropdown)
    {
      fieldName: 'CertificateOfOccupancyIssued',
      fieldType: FieldType.Select,
      value: 'No',
      addToActionItems: false,
    },
    {
      // Single / All / NoCalculation. Blank means Single (see parseScheduleCalcMode).
      fieldName: 'ScheduleCalculationMode',
      fieldType: FieldType.Select,
      value: '',
      addToActionItems: false,
    },
    {
      // Holds the drawId of the G702 driving the Single-mode calculation. Older reports hold a
      // draw name instead; resolveScheduleBudgetDraw accepts both.
      fieldName: 'WhichBudgetShouldBeUsedToCalculateTheSchedule',
      fieldType: FieldType.Select,
      value: '',
      addToActionItems: false,
    },

    // Calculated/Input fields
    {
      // Computed in ScheduleSection from the selected budget(s) — never seeded with a figure.
      fieldName: 'CalcWeeksAheadBehind',
      fieldType: FieldType.Text,
      value: '',
      addToActionItems: false,
    },
    {
      fieldName: 'WeeksBehindToReport',
      fieldType: FieldType.Text,
      value: '',
      addToActionItems: false,
    },

    // Boolean checkboxes
    {
      fieldName: 'CanTheyMakeItUp',
      fieldType: FieldType.Boolean,
      value: 'false',
      addToActionItems: false,
    },
    {
      fieldName: 'WillTheBorrowerAssessLiquidatedDamages',
      fieldType: FieldType.Boolean,
      value: 'false',
      addToActionItems: false,
    },
    {
      fieldName: 'CommentOnLaborMaterials',
      fieldType: FieldType.Boolean,
      value: 'false',
      addToActionItems: false,
    },
    {
      fieldName: 'LaborMaterialsAdequate',
      fieldType: FieldType.Boolean,
      value: 'false',
      addToActionItems: false,
    },

    // Schedule section text
    {
      fieldName: 'ScheduledSectionText',
      fieldType: FieldType.Text,
      value: '',
      addToActionItems: false,
    },

    // Construction progress schedule text
    {
      fieldName: 'ConstructionProgressScheduleText',
      fieldType: FieldType.Text,
      value: getConstructionProgressScheduleText(
        projectSummary?.commencementDate,
        projectSummary?.revisedCompletionDate || projectSummary?.originalCompletionDateEstimated,
      ),
      addToActionItems: false,
    },

    // Whether the dates in the schedule are estimates
    {
      fieldName: 'DatesAreEstimated',
      fieldType: FieldType.Boolean,
      value: 'false',
      addToActionItems: false,
    },

    // Comments section
    {
      fieldName: 'Comments',
      fieldType: FieldType.TextArray,
      entries: [],
    },
  ],
});

/**
 * Default data generator for Progress Description section
 */
export const getDefaultProgressDescriptionData = (): SectionFieldCollection => ({
  fields: [
    {
      fieldName: 'CurrentProgress',
      fieldType: FieldType.Text,
      value: '',
      addToActionItems: false,
    },
    {
      fieldName: 'Comments',
      fieldType: FieldType.TextArray,
      entries: [],
    },
  ],
});

/**
 * Default data generator for Minor Adjustments section
 */
export const getDefaultMinorAdjustmentsData = (): SectionFieldCollection => ({
  fields: [
    {
      fieldName: 'MinorAdjustmentsText',
      fieldType: FieldType.Text,
      value: '',
      addToActionItems: false,
    },
    {
      fieldName: 'Threshold',
      fieldType: FieldType.Number,
      value: '50',
    },
    {
      fieldName: 'Comments',
      fieldType: FieldType.TextArray,
      entries: [],
    },
  ],
});

export const getDefaultDocumentsReceivedData = (): SectionFieldCollection => ({
  fields: [
    ...DOCUMENTS_RECEIVED_DEFAULT_TYPES.map(doc => ({
      fieldName: doc.fieldName,
      fieldType: FieldType.Select,
      value: 'No',
      addToActionItems: false,
    })),
    {
      fieldName: 'CommentOnLoanDocs',
      fieldType: FieldType.Boolean,
      value: 'false',
      addToActionItems: false,
    },
    {
      fieldName: 'DocumentsReceivedIntroComment',
      fieldType: FieldType.Text,
      value: '',
      addToActionItems: false,
    },
    {
      fieldName: 'AllDocsComment',
      fieldType: FieldType.Text,
      value: '',
      addToActionItems: false,
    },
    {
      fieldName: 'LoanDocsComment',
      fieldType: FieldType.Text,
      value: '',
      addToActionItems: false,
    },
    {
      fieldName: 'Comments',
      fieldType: FieldType.TextArray,
      entries: [],
    },
  ],
});

export const FOUNDATION_SURVEY_COMMENTS = {
  formworkNo:
    'If the lender requests, once the foundation formwork has been erected or the foundation has been constructed, we will notify the borrower and procure a Survey to confirm that permanent structures do not encroach upon property lines, easements, setbacks, flood plains, or other restrictive areas.',
  formworkYesDefault:
    'The foundation formwork has been erected, or the foundations have been constructed. Please notify us if the Lender requires a Survey to confirm that the building is not encroaching upon restricted areas.',
  formworkYesSurveyYes:
    'We have requested and were provided a copy of a Survey showing or stating that permanent structures do not encroach upon property lines, easements, setbacks, flood plains, or other restrictive areas. No encroachments or exceptions were noted on the Survey.',
  formworkYesBankDeclined:
    'We understand that the lender does not need CDCC to procure or review the as-built survey to confirm that permanent structures do not encroach upon property lines, easements, setbacks, flood plains, or other restrictive areas.',
};

export const getFoundationSurveyDefaultText = (formworkSet: string, gotSurvey: string, bankDeclined: string) => {
  if (formworkSet !== 'Yes') {
    return FOUNDATION_SURVEY_COMMENTS.formworkNo;
  }

  if (bankDeclined === 'Yes') {
    return FOUNDATION_SURVEY_COMMENTS.formworkYesBankDeclined;
  }

  if (gotSurvey === 'Yes') {
    return FOUNDATION_SURVEY_COMMENTS.formworkYesSurveyYes;
  }

  return FOUNDATION_SURVEY_COMMENTS.formworkYesDefault;
};

export const getDefaultFoundationSurveyData = (): SectionFieldCollection => ({
  fields: [
    {
      fieldName: 'FormworkSet',
      fieldType: FieldType.Select,
      value: 'No',
      addToActionItems: false,
    },
    {
      fieldName: 'GotSurvey',
      fieldType: FieldType.Select,
      value: 'No',
      addToActionItems: false,
    },
    {
      fieldName: 'BankDeclined',
      fieldType: FieldType.Select,
      value: 'No',
      addToActionItems: false,
    },
    {
      fieldName: 'FoundationSurveyContent',
      fieldType: FieldType.Text,
      value: FOUNDATION_SURVEY_COMMENTS.formworkNo,
      addToActionItems: false,
    },
    {
      fieldName: 'Comments',
      fieldType: FieldType.TextArray,
      entries: [],
    },
  ],
});

/**
 * ============================================================================
 * Application Comments Section
 * ============================================================================
 */

export const getDefaultApplicationCommentsData = (): SectionFieldCollection => ({
  fields: [
    {
      fieldName: 'MinorDiscrepancy',
      fieldType: FieldType.TextArray,
      entries: [],
    },
    {
      fieldName: 'ShopDrawings',
      fieldType: FieldType.TextArray,
      entries: [],
    },
    {
      fieldName: 'Deposit',
      fieldType: FieldType.TextArray,
      entries: [],
    },
    {
      fieldName: 'Comments',
      fieldType: FieldType.TextArray,
      entries: [],
    },
  ],
});

export const APPLICATION_COMMENTS_SECTION_CONFIG: CompleteSectionConfig = {
  id: 'application-comments',
  title: 'Application Comments',
  getDefaultData: getDefaultApplicationCommentsData,
  config: {
    isCommentField: (fieldName: string, fieldType: FieldType) => fieldType === FieldType.TextArray && fieldName === 'Comments',
  },
  layout: {
    singleColumn: [
      {
        fieldName: 'MinorDiscrepancy',
        type: 'text-array',
        label: 'Minor Discrepancy',
        defaultValue:
          "We observed a minor discrepancy between the Contractor's application amount and our observations of the work at the site on the [Description] Line Item. Since the amount of the discrepancy is minor (< $10,000) we have not adjusted our recommendation for funding. However, we leave funding of the amount of this discrepancy to the discretion of the Lender.",
      },
      {
        fieldName: 'ShopDrawings',
        type: 'text-array',
        label: 'Shop Drawings',
        defaultValue:
          'A relatively minor amount has been requested for funding on the [Description] line item for Shop Drawings. Our policy is not to recommend funding on Shop Drawings until they have been completed and approved by the professional (Architect or Engineer) of record. Substantiating receipts, proposals, contracts, or other documentation specifying the request have been provided. We understand that the Shop Drawings have been approved and our recommendation for funding includes this amount.',
      },
      {
        fieldName: 'Deposit',
        type: 'text-array',
        label: 'Deposit',
        defaultValue:
          'A relatively minor amount has been requested for funding on the [Description] line item for an Initial Deposit. Our policy is not to recommend funding Initial Deposits without substantiating receipts, proposals, contracts, or other documentation specifying the request is required. We were provided with such documentation and our recommendation for funding includes this amount.',
      },
    ],
  },
};

/**
 * ============================================================================
 * Reduction Comments Section
 * ============================================================================
 */

export const getDefaultReductionCommentsData = (): SectionFieldCollection => ({
  fields: [
    {
      fieldName: 'ReductionCommentsRows',
      fieldType: FieldType.TextArray,
      entries: [],
      addToActionItems: false,
    },
    {
      fieldName: 'ZeroWorkComments',
      fieldType: FieldType.Text,
      value: '',
      addToActionItems: false,
    },
    {
      fieldName: 'PartialReductionComments',
      fieldType: FieldType.Text,
      value: '',
      addToActionItems: false,
    },
    {
      fieldName: 'Comments',
      fieldType: FieldType.TextArray,
      entries: [],
    },
  ],
});

export const REDUCTION_COMMENTS_SECTION_CONFIG: CompleteSectionConfig = {
  id: 'reduction-comments',
  title: 'Reduction Comments',
  getDefaultData: getDefaultReductionCommentsData,
  config: {
    isCommentField: (fieldName: string, fieldType: FieldType) => fieldType === FieldType.TextArray && fieldName === 'Comments',
  },
};

/**
 * ============================================================================
 * Complete Section Configurations
 * ============================================================================
 */

export const NOTARY_SECTION_CONFIG: CompleteSectionConfig = {
  id: 'notaryAndSignatures',
  title: 'Signatures and Notary',
  getDefaultData: getDefaultNotaryAndSignaturesData,
  config: NOTARY_SIGNATURES_CONFIG,
  fieldOptions: {
    ContractorSignature: ['Signed', 'Notarized'],
    ArchEngSignature: ['Signed', 'Architect Reviewed', 'Unsure if Architect Reviewed'],
  },

  layout: {
    singleColumn: [
      // Contractor Signature Group
      {
        fieldName: 'ContractorSignature',
        type: 'multiselect-toggle',
        label: 'Contractor Signature',
        options: ['Signed', 'Notarized'],
        pairedWith: 'ContractorSignatureContent',
        autoSelectRules: { option: 'Notarized', autoSelect: ['Signed'] },
        getDefaultText: (selectedValues: string[]) => {
          const hasNotarized = selectedValues.includes('Notarized');
          const hasSigned = selectedValues.includes('Signed');

          if (hasNotarized) {
            return 'The Application for Payment has been signed and dated by the Contractor and notarized as required.';
          } else if (hasSigned) {
            return 'The Application for Payment has been signed by the Contractors but not notarized. We leave the signature requirements as a condition for funding the Applications to the discretion of the Lender.';
          } else {
            return 'The Application for Payment has not been signed or notarized by the Contractor. We leave the signature requirements as a condition for funding the Applications to the discretion of the Lender.';
          }
        },
      },
      {
        fieldName: 'ContractorSignatureContent',
        type: 'text-paired',
        label: 'Contractor Signature Content',
      },
      // Arch Eng Signature Group
      {
        fieldName: 'ArchEngSignature',
        type: 'multiselect-toggle',
        label: 'Arch Eng Signature',
        options: ['Signed', 'Architect Reviewed', 'Unsure if Architect Reviewed'],
        mutuallyExclusive: [['Architect Reviewed', 'Unsure if Architect Reviewed']],
        pairedWith: 'ArchEngSignatureContent',
        isOptionDisabled: (option: string, selectedValues: string[]) => {
          // Signed should only be available when Architect Reviewed is selected
          if (option === 'Signed') {
            return !selectedValues.includes('Architect Reviewed');
          }
          return false;
        },
        getDefaultText: (selectedValues: string[]) => {
          const isSigned = selectedValues.includes('Signed');
          const isReviewed = selectedValues.includes('Architect Reviewed');
          const isUnsure = selectedValues.includes('Unsure if Architect Reviewed');

          if (isSigned && isReviewed) {
            return "The Architect of Record has certified the Amount of the Contractor's current Application.";
          } else if (isReviewed && !isSigned) {
            // Reviewed + NOT Signed
            return 'We understand that the Architect of Record is reviewing the Applications for Payment, but we have not received a copy of the certified document. We leave the Certification requirements as a condition for funding the Application to the discretion of the Lender.';
          } else if (!isSigned && isUnsure) {
            return "We are unaware if the Architect of Record is, or is not, reviewing the Application for Payment from the Contractor, or is providing Construction Administration services to fulfill their contractual obligations to the Borrower and/or confirm the quality and strict compliance of the Work to the Plans and Specifications. The funding of the current Application without Certification by the Architect is left to the Lender's discretion.";
          } else if (!isSigned && !isReviewed) {
            return "We understand that the Architect of Record is not reviewing the Applications for Payment from the Contractor. We recommend that they do so in order to provide subsequent quality control to that provided by the Contractor. The funding of each Application without Certification by the Architect is left to the Lender's discretion.";
          } else {
            return '';
          }
        },
      },
      {
        fieldName: 'ArchEngSignatureContent',
        type: 'text-paired',
        label: 'Arch Eng Signature Content',
      },
    ],
  },
};

export const SCHEDULE_SECTION_CONFIG: CompleteSectionConfig = {
  id: 'schedule',
  title: 'Construction Schedule',
  getDefaultData: getDefaultScheduleData,
  config: SCHEDULE_CONFIG,
  fieldOptions: {
    CertificateOfOccupancyIssued: ['Yes', 'No'],
    // ScheduleCalculationMode / WhichBudgetShouldBeUsedToCalculateTheSchedule options are built
    // from the report's covered G702s at render time (see ScheduleSection.tsx).
    ScheduleCalculationMode: [],
    WhichBudgetShouldBeUsedToCalculateTheSchedule: [],
  },
  layout: {
    // Left column: values that impact the generated report comments/text.
    // showBookmark: false on these fields — they're bookmarked implicitly via the section's
    // master action-item toggle (see ScheduleSection.tsx) rather than individually.
    leftColumn: [
      { fieldName: 'CommencementDate', type: 'custom-component', label: 'Commencement Date', showBookmark: false },
      { fieldName: 'OriginalCompletionDate', type: 'date', label: 'Original Completion Date', showBookmark: false },
      { fieldName: 'RevisedCompletionDate', type: 'date', label: 'Revised Completion Date', showBookmark: false },
      {
        fieldName: 'CDCCEstimatedCompletionDate',
        type: 'custom-component',
        label: 'CDCC Estimated Completion Date',
        showBookmark: false,
      },
      { fieldName: 'WeeksBehindToReport', type: 'custom-component', label: 'Weeks Behind to Report', showBookmark: false },
      { fieldName: 'BankLoanExpirationDate', type: 'date', label: 'Bank Loan Expiration Date', showBookmark: false },
      { fieldName: 'LiquidatedDamagesPerContract', type: 'text', label: 'Liquidated Damages per Contract', showBookmark: false },
      {
        fieldName: 'CertificateOfOccupancyIssued',
        type: 'select',
        label: 'Certificate of Occupancy Issued?',
        options: ['Yes', 'No'],
        showBookmark: false,
      },
      {
        fieldName: 'WhichBudgetShouldBeUsedToCalculateTheSchedule',
        type: 'select',
        label: 'Budget used for calculation',
        options: [],
        showBookmark: false,
      },
    ],
    // Right column: inspector aids only — none of these feed the generated report text.
    // Checkboxes are pushed down (see ScheduleSection.tsx spacer override) to visually align
    // with Liquidated Damages per Contract in the left column.
    rightColumn: [
      {
        fieldName: 'CalcWeeksAheadBehind',
        type: 'readonly',
        label: 'Calc Weeks Ahead (Behind) based on GC Numbers',
        formatValue: formatNegativeWithBrackets,
        showBookmark: false,
      },
      { fieldName: 'CanTheyMakeItUp', type: 'checkbox', label: 'Can they make it up?', boldLabel: true, showBookmark: false },
      {
        fieldName: 'WillTheBorrowerAssessLiquidatedDamages',
        type: 'checkbox',
        label: 'Will the Borrower assess Liquidated Damages?',
        boldLabel: true,
        showBookmark: false,
      },
      { fieldName: 'CommentOnLaborMaterials', type: 'checkbox', label: 'Comment on Labor/Materials?', showBookmark: false },
      { fieldName: 'LaborMaterialsAdequate', type: 'checkbox', label: 'Labor/Materials Adequate?', showBookmark: false },
    ],
    bottomSection: [{ fieldName: 'ScheduledSectionText', type: 'text', label: 'Schedule Section Text' }],
  },
};

export const PROGRESS_DESCRIPTION_SECTION_CONFIG: CompleteSectionConfig = {
  id: 'progressDescription',
  title: 'Description of Construction Progress',
  getDefaultData: getDefaultProgressDescriptionData,
  config: DEFAULT_SECTION_CONFIG,
  layout: {
    singleColumn: [
      {
        fieldName: 'CurrentProgress',
        type: 'text',
        label: 'Current Progress',
        hideLabel: true,
      },
    ],
  },
};

/**
 * Default data generator for Project Description section
 * A single free-text field. Not an action item (no bookmark) and no Comments field.
 */
export const getDefaultProjectDescriptionData = (): SectionFieldCollection => ({
  fields: [
    {
      fieldName: 'ProjectDescriptionText',
      fieldType: FieldType.Text,
      value: '',
    },
  ],
});

export const PROJECT_DESCRIPTION_SECTION_CONFIG: CompleteSectionConfig = {
  id: 'projectDescription',
  title: 'Project Description',
  getDefaultData: getDefaultProjectDescriptionData,
  config: DEFAULT_SECTION_CONFIG,
  hideActionItemBookmark: true,
  layout: {
    singleColumn: [
      {
        fieldName: 'ProjectDescriptionText',
        type: 'text',
        label: 'Project Description',
        hideLabel: true,
        showBookmark: false,
      },
    ],
  },
};

export const PLAN_COST_REVIEW_SECTION_CONFIG: CompleteSectionConfig = {
  id: 'planCostReviewIssues',
  title: 'Initial Plan/Cost Review Issues',
  getDefaultData: getDefaultPlanCostReviewData,
  config: DEFAULT_SECTION_CONFIG,
  fieldOptions: {
    PlanCostReviewStatus: [...PLAN_COST_REVIEW_TOGGLE_OPTIONS],
  },
  layout: {
    singleColumn: [
      {
        fieldName: 'PlanCostReviewPrimaryText',
        type: 'text',
        label: 'Plan/Cost Review Summary',
      },
      {
        fieldName: 'PlanCostReviewSecondaryText',
        type: 'text',
        label: 'Plan/Cost Review Notes',
      },
    ],
  },
};

export const MINOR_ADJUSTMENTS_SECTION_CONFIG: CompleteSectionConfig = {
  id: 'minorAdjustments',
  title: 'Minor Adjustments',
  getDefaultData: getDefaultMinorAdjustmentsData,
  config: DEFAULT_SECTION_CONFIG,
  // Note: Layout is defined dynamically in the component due to runtime dependencies
};

export const DOCUMENTS_RECEIVED_SECTION_CONFIG: CompleteSectionConfig = {
  id: 'docsReceived',
  title: 'Documents Received',
  getDefaultData: getDefaultDocumentsReceivedData,
  config: DEFAULT_SECTION_CONFIG,
  layout: {
    leftColumn: [
      {
        fieldName: 'CivilPlans',
        type: 'select',
        label: 'Civil Plans',
        options: DOCUMENTS_RECEIVED_STATUS_OPTIONS,
        showBookmark: false,
        inlineLayout: true,
      },
      {
        fieldName: 'ArchitecturalPlans',
        type: 'select',
        label: 'Architectural Plans',
        options: DOCUMENTS_RECEIVED_STATUS_OPTIONS,
        showBookmark: false,
        inlineLayout: true,
      },
      {
        fieldName: 'StructuralPlans',
        type: 'select',
        label: 'Structural Plans',
        options: DOCUMENTS_RECEIVED_STATUS_OPTIONS,
        showBookmark: false,
        inlineLayout: true,
      },
      {
        fieldName: 'MechanicalPlans',
        type: 'select',
        label: 'Mechanical Plans',
        options: DOCUMENTS_RECEIVED_STATUS_OPTIONS,
        showBookmark: false,
        inlineLayout: true,
      },
      {
        fieldName: 'PlumbingPlans',
        type: 'select',
        label: 'Plumbing Plans',
        options: DOCUMENTS_RECEIVED_STATUS_OPTIONS,
        showBookmark: false,
        inlineLayout: true,
      },
      {
        fieldName: 'BuildersRiskGeneralLiabilityCerts',
        type: 'select',
        label: "Builder's Risk and General Liability Certs.",
        options: DOCUMENTS_RECEIVED_STATUS_OPTIONS,
        showBookmark: false,
        inlineLayout: true,
      },
      {
        fieldName: 'FormSurvey',
        type: 'select',
        label: 'Form Survey',
        options: DOCUMENTS_RECEIVED_STATUS_OPTIONS,
        showBookmark: false,
        inlineLayout: true,
      },
    ],
    rightColumn: [
      {
        fieldName: 'ElectricalPlans',
        type: 'select',
        label: 'Electrical Plans',
        options: DOCUMENTS_RECEIVED_STATUS_OPTIONS,
        showBookmark: false,
        inlineLayout: true,
      },
      {
        fieldName: 'Specifications',
        type: 'select',
        label: 'Specifications',
        options: DOCUMENTS_RECEIVED_STATUS_OPTIONS,
        showBookmark: false,
        inlineLayout: true,
      },
      {
        fieldName: 'OwnerContractorAgreement',
        type: 'select',
        label: 'Owner Contractor Agreement',
        options: DOCUMENTS_RECEIVED_STATUS_OPTIONS,
        showBookmark: false,
        inlineLayout: true,
      },
      {
        fieldName: 'SiteDevelopmentPermit',
        type: 'select',
        label: 'Site Development Permit',
        options: DOCUMENTS_RECEIVED_STATUS_OPTIONS,
        showBookmark: false,
        inlineLayout: true,
      },
      {
        fieldName: 'BuildingPermit',
        type: 'select',
        label: 'Building Permit',
        options: DOCUMENTS_RECEIVED_STATUS_OPTIONS,
        showBookmark: false,
        inlineLayout: true,
      },
      {
        fieldName: 'LoanDocumentsHardCost',
        type: 'select',
        label: 'Loan documents related to Hard Cost funding',
        options: DOCUMENTS_RECEIVED_STATUS_OPTIONS,
        showBookmark: false,
        inlineLayout: true,
      },
    ],
  },
};

/**
 * Configuration for Material Testing Reports section
 */
export const MATERIAL_TESTING_REPORTS_SECTION_CONFIG: CompleteSectionConfig = {
  id: 'materialTestingReports',
  title: 'Materials Testing',
  getDefaultData: () => ({
    fields: [
      {
        fieldName: 'MaterialTestingTests',
        fieldType: FieldType.TextArray,
        entries: [],
      },
      {
        fieldName: 'MaterialTestingAutoComment',
        fieldType: FieldType.Text,
        value: '',
        addToActionItems: false,
      },
      {
        fieldName: 'MaterialTestingFailedTestsComment',
        fieldType: FieldType.Text,
        value: '',
        addToActionItems: false,
      },
      {
        fieldName: 'MaterialTestingAllPassComment',
        fieldType: FieldType.Text,
        value: '',
        addToActionItems: false,
      },
      {
        fieldName: 'MaterialTestingRecommendationComment',
        fieldType: FieldType.Text,
        value: '',
        addToActionItems: false,
      },
      {
        fieldName: 'MaterialTestingRemainingTestsPassed',
        fieldType: FieldType.Text,
        value: 'Yes',
        addToActionItems: false,
      },
      {
        fieldName: 'RequireTestingForRetainageRelease',
        fieldType: FieldType.Text,
        value: 'No',
        addToActionItems: false,
      },
      {
        fieldName: 'MaterialTestingEngineerResponding',
        fieldType: FieldType.Text,
        value: 'Yes',
        addToActionItems: false,
      },
      {
        fieldName: 'MaterialTestingProviderComment',
        fieldType: FieldType.Text,
        value: '',
        addToActionItems: false,
      },
      {
        fieldName: 'Comments',
        fieldType: FieldType.TextArray,
        entries: [],
      },
    ],
  }),
  config: {
    isCommentField: (name, type) => type === FieldType.TextArray && name === 'Comments',
  },
  // Layout will be set dynamically in the component to pass necessary props
  layout: {
    singleColumn: [],
  },
};

/**
 * Configuration for Construction Meeting Minutes section
 */
export const CONSTRUCTION_MEETING_MINUTES_SECTION_CONFIG: CompleteSectionConfig = {
  id: 'constructionMeetingMinutes',
  title: 'Construction Meeting Minutes',
  getDefaultData: () => ({
    fields: [
      {
        fieldName: 'ConstructionMeetingMinutesComment',
        fieldType: FieldType.Text,
        value: '',
        addToActionItems: false,
      },
    ],
  }),
  config: DEFAULT_SECTION_CONFIG,
  layout: {
    singleColumn: [],
  },
};

export const FOUNDATION_SURVEY_SECTION_CONFIG: CompleteSectionConfig = {
  id: 'foundationSurvey',
  title: 'Foundation Survey',
  getDefaultData: getDefaultFoundationSurveyData,
  config: DEFAULT_SECTION_CONFIG,
};

/**
 * Default data generator for Retainage section
 */
export const getDefaultRetainageData = (): SectionFieldCollection => ({
  fields: [
    {
      fieldName: 'ShowList',
      fieldType: FieldType.Boolean,
      value: 'false',
      addToActionItems: false,
    },
    {
      fieldName: 'ReleaseRetainageLanguage',
      fieldType: FieldType.Boolean,
      value: 'false',
      addToActionItems: false,
    },
    {
      fieldName: 'LargestReleases',
      fieldType: FieldType.Boolean,
      value: 'false',
      addToActionItems: false,
    },
    {
      fieldName: 'RetainagePercentage',
      fieldType: FieldType.Number,
      value: '',
      addToActionItems: false,
      lastMonth: '', // Last month's retainage percentage (populated by backend on reset)
    },
    {
      fieldName: 'MainComment',
      fieldType: FieldType.Text,
      value: '',
      addToActionItems: false,
    },
    {
      fieldName: 'ProceduresList',
      fieldType: FieldType.Text,
      value: '',
      addToActionItems: false,
    },
    {
      fieldName: 'ReleaseProceduresList',
      fieldType: FieldType.Text,
      value: '',
      addToActionItems: false,
    },
    {
      fieldName: 'ReleaseComments',
      fieldType: FieldType.Text,
      value: '',
      addToActionItems: false,
    },
    {
      fieldName: 'LargestReleasesComment',
      fieldType: FieldType.Text,
      value: '',
      addToActionItems: false,
    },
    {
      fieldName: 'Comments',
      fieldType: FieldType.TextArray,
      entries: [],
    },
  ],
});

export const RETAINAGE_SECTION_CONFIG: CompleteSectionConfig = {
  id: 'retainage',
  title: 'Retainage',
  getDefaultData: getDefaultRetainageData,
  config: {
    isCommentField: (fieldName: string, fieldType: FieldType) => fieldType === FieldType.TextArray && fieldName === 'Comments',
  },
  layout: {
    singleColumn: [
      {
        fieldName: 'MainComment',
        type: 'text',
        label: 'Retainage Comment',
      },
      {
        fieldName: 'ProceduresList',
        type: 'text',
        label: 'Procedures List',
      },
      {
        fieldName: 'ReleaseProceduresList',
        type: 'text',
        label: 'Release Procedures List',
      },
      {
        fieldName: 'ReleaseComments',
        type: 'text',
        label: 'Release Comments',
      },
      {
        fieldName: 'LargestReleasesComment',
        type: 'text',
        label: 'Largest Releases',
      },
    ],
  },
};

/**
 * ============================================================================
 * Stored Materials Section
 * ============================================================================
 */

export const STORED_MATERIALS_G703_NO_REQUEST_TEXT =
  'The Contractor has not made a specific request for funding of On-Site or Off-Site Stored Materials in the Application for Payment G703 Continuation Sheet.';

export const STORED_MATERIALS_G703_YES_OFFSITE_TEXT =
  'The Contractor has made a specific request for funding of Off-Site Stored Materials in the Application for Payment G703 Continuation Sheet as follows:';

export const STORED_MATERIALS_G703_YES_ONSITE_TEXT =
  'The Contractor has made a specific request for funding of On-Site Stored Materials in the Application for Payment G703 Continuation Sheet as follows:';

export const STORED_MATERIALS_G703_YES_MIXED_TEXT =
  'The Contractor has made a specific request for funding of On-Site and Off-Site Stored Materials in the Application for Payment G703 Continuation Sheet as follows:';

export const STORED_MATERIALS_OTHER_MATERIALS_INTRO_TEXT =
  'Materials that are stored On-Site, not noted in the Application for Payment G703 Continuation Sheet, but may not be incorporated into the Work within the next month include:';

export const STORED_MATERIALS_OTHER_MATERIALS_DEFAULT_TEXT = `${STORED_MATERIALS_OTHER_MATERIALS_INTRO_TEXT}\n\nNone`;

export const getDefaultStoredMaterialsData = (): SectionFieldCollection => ({
  fields: [
    {
      fieldName: 'StoredMaterialsRows',
      fieldType: FieldType.TextArray,
      entries: [],
      addToActionItems: false,
    },
    {
      fieldName: 'G703MaterialsIntroComment',
      fieldType: FieldType.Text,
      value: STORED_MATERIALS_G703_NO_REQUEST_TEXT,
      addToActionItems: false,
    },
    {
      fieldName: 'G703MaterialsComment',
      fieldType: FieldType.Text,
      value: '',
      addToActionItems: false,
    },
    {
      fieldName: 'OtherMaterialsIntro',
      fieldType: FieldType.Text,
      value: STORED_MATERIALS_OTHER_MATERIALS_INTRO_TEXT,
      addToActionItems: false,
    },
    {
      fieldName: 'OtherMaterialsList',
      fieldType: FieldType.Text,
      value: STORED_MATERIALS_OTHER_MATERIALS_DEFAULT_TEXT,
      addToActionItems: false,
    },
    {
      fieldName: 'Comments',
      fieldType: FieldType.TextArray,
      entries: [],
    },
  ],
});

export const STORED_MATERIALS_SECTION_CONFIG: CompleteSectionConfig = {
  id: 'storedMaterials',
  title: 'Stored Materials',
  getDefaultData: getDefaultStoredMaterialsData,
  config: DEFAULT_SECTION_CONFIG,
  layout: {
    singleColumn: [
      {
        fieldName: 'G703MaterialsIntroComment',
        type: 'text-with-refresh',
        label: 'G703 Materials Intro',
      },
      {
        fieldName: 'StoredMaterialsRows',
        type: 'custom-component',
        label: 'Stored Materials Table',
      },
      {
        fieldName: 'G703MaterialsComment',
        type: 'text-with-refresh',
        label: 'G703 Materials Comment',
      },
      {
        fieldName: 'OtherMaterialsIntro',
        type: 'readonly',
        label: 'List Other Materials Intro',
      },
      {
        fieldName: 'OtherMaterialsList',
        type: 'text-with-refresh',
        label: 'List Other Materials',
      },
    ],
  },
};

/**
 * ============================================================================
 * Reallocations Section
 * ============================================================================
 */

export const getDefaultReallocationsData = (): SectionFieldCollection => ({
  fields: [
    {
      fieldName: 'ReallocationsRows',
      fieldType: FieldType.TextArray,
      entries: [],
    },
    {
      fieldName: 'ReallocationsComment1',
      fieldType: FieldType.Text,
      value: '',
      addToActionItems: false,
    },
    {
      fieldName: 'ReallocationsComment2',
      fieldType: FieldType.Text,
      value: '',
      addToActionItems: false,
    },
    {
      fieldName: 'Comments',
      fieldType: FieldType.TextArray,
      entries: [],
    },
  ],
});

export const REALLOCATIONS_SECTION_CONFIG: CompleteSectionConfig = {
  id: 'reallocations',
  title: 'Reallocations',
  getDefaultData: getDefaultReallocationsData,
  config: DEFAULT_SECTION_CONFIG,
  // Layout is defined dynamically in the component due to runtime dependencies
};

/**
 * ============================================================================
 * Change Orders Section
 * ============================================================================
 */

export const getDefaultChangeOrdersData = (): SectionFieldCollection => ({
  fields: [
    {
      fieldName: 'ChangeOrdersRows',
      fieldType: FieldType.TextArray,
      entries: [],
    },
    {
      fieldName: 'ChangeOrdersComment1',
      fieldType: FieldType.Text,
      value: '',
      addToActionItems: false,
    },
    {
      fieldName: 'ChangeOrdersComment2',
      fieldType: FieldType.Text,
      value: '',
      addToActionItems: false,
    },
    {
      fieldName: 'ChangeOrdersSummaryOriginalContractSum',
      fieldType: FieldType.Number,
      value: '0',
      addToActionItems: false,
    },
    {
      fieldName: 'ChangeOrdersSummaryTotalNetChangeOrder',
      fieldType: FieldType.Number,
      value: '0',
      addToActionItems: false,
    },
    {
      fieldName: 'ChangeOrdersSummaryTotalNetReallocation',
      fieldType: FieldType.Number,
      value: '0',
      addToActionItems: false,
    },
    {
      fieldName: 'ChangeOrdersSummaryContractSumToDate',
      fieldType: FieldType.Number,
      value: '0',
      addToActionItems: false,
    },
    {
      fieldName: 'Comments',
      fieldType: FieldType.TextArray,
      entries: [],
    },
  ],
});

export const CHANGE_ORDERS_SECTION_CONFIG: CompleteSectionConfig = {
  id: 'changeOrders',
  title: 'Change Orders',
  getDefaultData: getDefaultChangeOrdersData,
  config: DEFAULT_SECTION_CONFIG,
  // Layout is defined dynamically in the component due to runtime dependencies
};

/**
 * Configuration for Potential Change Orders section
 */
export const POTENTIAL_CHANGE_ORDERS_SECTION_CONFIG: CompleteSectionConfig = {
  id: 'potentialChangeOrders',
  title: 'Potential Change Orders',
  getDefaultData: () => ({
    fields: [
      {
        fieldName: 'PotentialChangeOrdersComment',
        fieldType: FieldType.Text,
        value: '',
        addToActionItems: false,
      },
    ],
  }),
  config: DEFAULT_SECTION_CONFIG,
  layout: {
    singleColumn: [],
  },
};

/**
 * ============================================================================
 * Lien Waivers Section
 * ============================================================================
 */

// The single-payapp wording now lives with the comment engine that produces every variant of it.
// Aliased here so existing importers of these names keep working.
export const LIEN_WAIVERS_CONDITIONAL_COMMENT = LIEN_WAIVERS_SINGLE_CONDITIONAL_COMMENT;
export const LIEN_WAIVERS_NO_WAIVER_COMMENT = LIEN_WAIVERS_SINGLE_NO_WAIVER_COMMENT;
export const LIEN_WAIVERS_UNCONDITIONAL_ABOVE_TABLE_COMMENT = LIEN_WAIVERS_SINGLE_ABOVE_TABLE_COMMENT;
export const LIEN_WAIVERS_UNCONDITIONAL_BELOW_TABLE_COMMENT = LIEN_WAIVERS_SINGLE_UNCONDITIONAL_COMMENT;

export const getDefaultLienWaiversData = (): SectionFieldCollection => ({
  fields: [
    {
      fieldName: 'LienWaiverStatus',
      fieldType: FieldType.Select,
      value: 'Conditional', // Default
      addToActionItems: false,
    },
    {
      fieldName: 'IsCumulative',
      fieldType: FieldType.Boolean,
      value: 'false',
      addToActionItems: false,
    },
    {
      fieldName: 'ThroughCurrentPeriod',
      fieldType: FieldType.Boolean,
      value: 'false',
      addToActionItems: false,
    },
    {
      fieldName: 'LienWaiverComment',
      fieldType: FieldType.Text,
      value: LIEN_WAIVERS_CONDITIONAL_COMMENT,
      addToActionItems: false,
    },
    {
      fieldName: 'LienWaiverAboveTableComment',
      fieldType: FieldType.Text,
      value: '',
      addToActionItems: false,
    },
    {
      fieldName: 'LienWaiverBelowTableComment',
      fieldType: FieldType.Text,
      value: '',
      addToActionItems: false,
    },
    {
      fieldName: 'LienWaiverApplicationDate',
      fieldType: FieldType.Text,
      value: '',
      addToActionItems: false,
    },
    {
      fieldName: 'LienWaiverApplicationNumber',
      fieldType: FieldType.Text,
      value: '',
      addToActionItems: false,
    },
    {
      fieldName: 'LienWaiverAmount',
      fieldType: FieldType.Text,
      value: '',
      addToActionItems: false,
    },
    {
      fieldName: 'LienWaiverContractor',
      fieldType: FieldType.Text,
      value: '',
      addToActionItems: false,
    },
    {
      fieldName: 'LienWaiverIsManuallyEdited',
      fieldType: FieldType.Boolean,
      value: 'false',
      addToActionItems: false,
    },
    {
      fieldName: 'LienWaiverHistory',
      fieldType: FieldType.Text,
      value: '[]',
      addToActionItems: false,
    },
    // Per-payapp waiver state, tagged by drawId. See lien-waivers-draws.util.ts.
    // The scalar fields above remain a mirror of the primary payapp.
    {
      fieldName: 'LienWaiverDraws',
      fieldType: FieldType.Text,
      value: '[]',
      addToActionItems: false,
    },
    {
      fieldName: 'Comments',
      fieldType: FieldType.TextArray,
      entries: [],
    },
  ],
});

export const LIEN_WAIVERS_SECTION_CONFIG: CompleteSectionConfig = {
  id: 'lienWaivers',
  title: 'Lien Releases / Waivers',
  getDefaultData: getDefaultLienWaiversData,
  config: {
    isCommentField: (fieldName: string, fieldType: FieldType) => fieldType === FieldType.TextArray && fieldName === 'Comments',
  },
  layout: {
    singleColumn: [],
  },
};
